import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fabricsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileSpreadsheet, Check, X, AlertTriangle, RefreshCw, Image, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

export default function FabricImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (!f.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(f);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await fabricsAPI.importPreview(file);
      setPreview(response.data);
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    setImporting(true);
    setStep('importing');
    try {
      const response = await fabricsAPI.importConfirm(file);
      setResult(response.data);
      setStep('done');
      const total = (response.data.inserted || 0) + (response.data.updated || 0);
      if (total > 0) {
        toast.success(`Imported ${total} fabric(s)`);
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} row(s) had errors`);
      }
    } catch (err) {
      toast.error('Import failed');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/fabrics')} className="h-9 px-2">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Bulk Import Fabrics</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-muted)' }}>Import fabrics from a CSV file</p>
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
          <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Upload CSV File</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div
              className="border-2 border-dashed border-border/60 rounded-xl p-12 text-center cursor-pointer hover:border-[color:var(--brand-gold)] hover:bg-[rgba(197,165,90,0.02)]"
              style={{ transition: 'border-color 220ms, background-color 220ms' }}
              onClick={() => document.getElementById('csv-upload').click()}
              data-testid="csv-upload-dropzone"
            >
              <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" id="csv-upload" data-testid="csv-file-input" />
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--brand-navy)', opacity: 0.5 }} strokeWidth={1.5} />
              {file ? (
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{file.name}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Click to change file</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Click to select a CSV file</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>or drag and drop</p>
                </div>
              )}
            </div>

            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-2)' }}>
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--navy-ink)' }}>Accepted CSV columns (auto-mapped):</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                Internal Code, Vendor Code, Mill, Composition, Weight (GSM), Width (cm), Primary Colour, Secondary Colour, Pattern, Occasion Tags, Season, Buying Price/m (INR), Price Tier, Vendor, Lead Time (days), Available, Body Types, Complexions, Notes, Swatch Image URL
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--ink-muted)' }}>
                <strong>Required:</strong> Internal Code, Vendor Code, Mill, Composition, Width (cm), Primary Colour, Pattern, Season, Buying Price/m, Price Tier, Vendor, Lead Time
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                <strong>Duplicates:</strong> Existing fabrics matched by Internal Code will be updated. New codes will be inserted.
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                <strong>Prices:</strong> Accepts commas as separators (e.g., "15,750.00") and currency symbols (Rs., INR, ₹)
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate('/app/fabrics')} className="h-11 rounded-xl px-6">Cancel</Button>
              <Button onClick={handlePreview} disabled={!file || uploading} className="h-11 rounded-xl px-6"
                data-testid="preview-import-button"
                style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Parsing...' : 'Preview Import'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && preview && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{preview.total_rows}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Total Rows</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-semibold tabular-nums" style={{ color: 'var(--success)' }}>{preview.valid_rows}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Valid</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-semibold tabular-nums" style={{ color: '#2563EB' }}>{preview.new_count}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>New Fabrics</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-semibold tabular-nums" style={{ color: '#D97706' }}>{preview.update_count}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Updates</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border border-border/80 bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-semibold tabular-nums" style={{ color: preview.invalid_rows > 0 ? 'var(--danger)' : 'var(--ink-muted)' }}>{preview.invalid_rows}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Invalid</p>
              </CardContent>
            </Card>
          </div>

          {/* Columns found + unmapped */}
          <Card className="rounded-xl border border-border/80 bg-white">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--navy-ink)' }}>Column mapping:</p>
              <div className="flex flex-wrap gap-1.5">
                {preview.columns_found.map(col => (
                  <Badge key={col} variant="outline" className="text-xs px-2 py-0.5 rounded-md bg-[rgba(30,122,95,0.06)] text-[#1E7A5F] border-[rgba(30,122,95,0.2)]">
                    <Check className="w-3 h-3 mr-1" />{col}
                  </Badge>
                ))}
                {preview.columns_unmapped?.map(col => (
                  <Badge key={col} variant="outline" className="text-xs px-2 py-0.5 rounded-md bg-[rgba(245,158,11,0.08)] text-[#D97706] border-[rgba(245,158,11,0.2)]">
                    <AlertTriangle className="w-3 h-3 mr-1" />{col} (unmapped)
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preview Table */}
          <Card data-testid="import-preview-table" className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>
                Data Preview <span className="text-xs font-normal ml-2" style={{ color: 'var(--ink-muted)' }}>({preview.preview.length} rows)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[60vh] border-t">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-white">
                    <TableRow>
                      <TableHead className="text-xs w-10">#</TableHead>
                      <TableHead className="text-xs w-16">Status</TableHead>
                      <TableHead className="text-xs">Code</TableHead>
                      <TableHead className="text-xs">Mill</TableHead>
                      <TableHead className="text-xs">Colour</TableHead>
                      <TableHead className="text-xs">Pattern</TableHead>
                      <TableHead className="text-xs">Price</TableHead>
                      <TableHead className="text-xs">Image</TableHead>
                      <TableHead className="text-xs">Action</TableHead>
                      <TableHead className="text-xs">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.preview.map(row => (
                      <TableRow key={row.row_number} className={row.is_valid ? '' : 'bg-red-50/50'}>
                        <TableCell className="text-xs tabular-nums">{row.row_number}</TableCell>
                        <TableCell>
                          {row.is_valid ? (
                            <Check className="w-4 h-4" style={{ color: 'var(--success)' }} />
                          ) : (
                            <AlertTriangle className="w-4 h-4" style={{ color: 'var(--danger)' }} />
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-medium">{row.data.internal_code || '-'}</TableCell>
                        <TableCell className="text-xs">{row.data.mill_name || '-'}</TableCell>
                        <TableCell className="text-xs">{row.data.colour_primary || '-'}</TableCell>
                        <TableCell className="text-xs capitalize">{row.data.pattern_type || '-'}</TableCell>
                        <TableCell className="text-xs tabular-nums">
                          {row.data.price_per_metre ? `₹${Number(row.data.price_per_metre).toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.data.swatch_image_url ? (
                            <span className="text-green-600 flex items-center gap-0.5"><Image className="w-3 h-3" /> Yes</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Badge className="text-[9px] px-1.5 py-0 rounded-md"
                            style={{
                              backgroundColor: row.action === 'update' ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.08)',
                              color: row.action === 'update' ? '#D97706' : '#2563EB',
                            }}>
                            {row.action === 'update' ? 'Update' : 'New'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px]">
                          {row.errors.length > 0 && (
                            <span style={{ color: 'var(--danger)' }}>{row.errors.join('; ')}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setStep('upload'); setPreview(null); setFile(null); }} className="h-11 rounded-xl px-6">Back</Button>
            <Button data-testid="import-confirm-button" onClick={handleConfirmImport} disabled={preview.valid_rows === 0}
              className="h-11 rounded-xl px-6" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
              Import {preview.valid_rows} Valid Row{preview.valid_rows !== 1 ? 's' : ''}
              {preview.update_count > 0 && ` (${preview.new_count} new, ${preview.update_count} updates)`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-12 text-center space-y-4">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--brand-navy)] border-t-transparent rounded-full mx-auto" />
            <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Importing fabrics...</p>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              Processing {preview?.valid_rows || 0} rows. This may take a moment for large imports.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Done */}
      {step === 'done' && result && (
        <div className="space-y-4">
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(30,122,95,0.1)' }}>
                <Check className="w-7 h-7" style={{ color: 'var(--success)' }} />
              </div>
              <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>Import Complete</h2>

              {/* Summary */}
              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-semibold" style={{ color: '#2563EB' }}>{result.inserted}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>New</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold" style={{ color: '#D97706' }}>{result.updated}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Updated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-semibold" style={{ color: result.errors?.length > 0 ? 'var(--danger)' : 'var(--ink-muted)' }}>{result.errors?.length || 0}</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Failed</p>
                </div>
              </div>

              {/* Errors */}
              {result.errors?.length > 0 && (
                <div className="text-left rounded-lg p-4 max-h-[200px] overflow-auto" style={{ backgroundColor: 'rgba(180,35,24,0.04)' }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--danger)' }}>Failed rows:</p>
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-xs mb-1" style={{ color: 'var(--danger)' }}>Row {err.row}: {err.error}</p>
                  ))}
                </div>
              )}

              <Button onClick={() => navigate('/app/fabrics')} className="h-11 rounded-xl px-6 mt-4"
                data-testid="view-catalogue-button"
                style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
                View Catalogue
              </Button>
            </CardContent>
          </Card>

          {/* Sample Verification */}
          {result.sample_fabrics?.length > 0 && (
            <Card className="rounded-xl border border-border/80 bg-white" data-testid="import-sample-verification">
              <CardHeader>
                <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>
                  Sample Verification
                  <span className="text-xs font-normal ml-2" style={{ color: 'var(--ink-muted)' }}>5 random fabrics from catalogue</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-5 gap-0 divide-x">
                  {result.sample_fabrics.map((f, i) => (
                    <div key={i} className="p-3 text-center">
                      <div className="w-16 h-16 mx-auto rounded-lg overflow-hidden border mb-2">
                        <SwatchImage url={f.swatch_image_url} colour={f.colour_primary} alt={f.internal_code} />
                      </div>
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--navy-ink)' }}>{f.internal_code}</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{f.mill_name}</p>
                      {f.price_per_metre && (
                        <p className="text-[10px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>₹{Number(f.price_per_metre).toLocaleString('en-IN')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

/** Swatch image with lazy loading and error fallback */
function SwatchImage({ url, colour, alt, className = '' }) {
  const [error, setError] = useState(false);
  const colourHex = getColourHex(colour);

  if (!url || error) {
    return (
      <div className={`w-full h-full ${className}`}
        style={{ backgroundColor: colourHex }}
        title={colour || 'No image'} />
    );
  }

  return (
    <img
      src={resolveUrl(url)}
      alt={alt || ''}
      className={`w-full h-full object-cover ${className}`}
      loading="lazy"
      onError={() => setError(true)}
    />
  );
}

function getColourHex(colour) {
  if (!colour) return '#E5E7EB';
  const map = {
    navy: '#1B2A4A', black: '#1a1a1a', charcoal: '#36454F', grey: '#808080', gray: '#808080',
    blue: '#2563EB', brown: '#8B4513', tan: '#D2B48C', beige: '#F5F5DC', cream: '#FFFDD0',
    white: '#F8F8F8', burgundy: '#800020', red: '#B91C1C', green: '#166534', olive: '#808000',
    pink: '#EC4899', purple: '#7C3AED', wine: '#722F37', camel: '#C19A6B', ivory: '#FFFFF0',
    khaki: '#C3B091', gold: '#C5A55A', silver: '#C0C0C0', taupe: '#483C32', sage: '#B2AC88',
    rust: '#B7410E', teal: '#008080', coral: '#FF7F50', lavender: '#E6E6FA', plum: '#8E4585',
    indigo: '#4B0082', mustard: '#FFDB58', maroon: '#800000', chocolate: '#7B3F00',
    steel: '#71797E', midnight: '#191970', cobalt: '#0047AB', emerald: '#50C878',
  };
  const lower = (colour || '').toLowerCase().trim();
  return map[lower] || '#9CA3AF';
}
